import { Vector3 } from "three";
import type { PerspectiveCamera } from "three";
import { config } from "../../config";
import type { Axis2 } from "../../types/input";

const WORLD_UP = new Vector3(0, 1, 0);

/**
 * Où le curseur touche le sol, pour une pose de caméra donnée ([TD-050]).
 *
 * Reconstruit le rayon à la main plutôt que par `Raycaster` : la pose visée
 * n'est pas celle de la caméra, qui est amortie et donc toujours en retard.
 * Les vecteurs de travail sont conservés, la méthode étant appelée par image.
 */
export class GroundRay {
  private readonly forward = new Vector3();
  private readonly right = new Vector3();
  private readonly up = new Vector3();
  private readonly direction = new Vector3();

  constructor(private readonly camera: PerspectiveCamera) {}

  /**
   * Point du plan horizontal `planeY` visé par `ndc` depuis `position`, pour
   * une caméra regardant `target`. Faux si le rayon ne redescend pas vers le
   * plan, ou s'il le rencontre au-delà du plan lointain : près de l'horizon un
   * pixel vaut des centaines de mètres, et s'y accrocher n'a plus de sens.
   */
  hit(
    ndc: Readonly<Axis2>,
    position: Vector3,
    target: Vector3,
    planeY: number,
    out: Vector3,
  ): boolean {
    this.forward.copy(target).sub(position);
    if (this.forward.lengthSq() === 0) return false;
    this.forward.normalize();

    this.right.crossVectors(this.forward, WORLD_UP);
    if (this.right.lengthSq() < 1e-8) return false; // vue à la verticale
    this.right.normalize();
    this.up.crossVectors(this.right, this.forward);

    const tanHalfFov = Math.tan((this.camera.fov * Math.PI) / 360);
    this.direction
      .copy(this.forward)
      .addScaledVector(this.right, ndc.x * tanHalfFov * this.camera.aspect)
      .addScaledVector(this.up, ndc.y * tanHalfFov)
      .normalize();

    if (this.direction.y > -1e-4) return false; // parallèle au sol, ou vers le ciel
    const along = (planeY - position.y) / this.direction.y;
    if (along <= 0 || along > config.camera.far) return false;

    out.copy(position).addScaledVector(this.direction, along);
    return true;
  }
}
